import { Injectable } from '@angular/core';
import { LineInfoService } from './line-info.service';
import { QueryService } from './query.service';
import { EsriGeometryEngine } from '../../config/esri.module';
import { MessageService } from '../../core/service/message.service';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LayerCode } from '../enum/layer-code.enum';
import { LayerContainerService } from './layer-container.service';
import { TransaktionsObjektType } from '../enum/transaktions-objekt-type.enum';

/**
 * @description Handels the attributes form different layers.
 */
@Injectable({
  providedIn: 'root',
})
export class AttributesService {
  constructor(
    private lineInfoService: LineInfoService,
    private queryService: QueryService,
    private messageService: MessageService,
    private layerContainerService: LayerContainerService
  ) {}

  /**
   * @name getDefaultAttributes
   * @param layerId
   * @param graphic
   * @returns {Observable<any>}
   * @description Returns the default attributes based on the layer-id
   * If you need to set defaults on a specific feature, you have to extend this method.
   */
  public getDefaultAttributes(layerId: string, graphic: __esri.Graphic): Observable<any> {
    switch (layerId) {
      case LayerCode.f_verkaufsobjekte:
        return this.getAttributes(graphic);
      default:
        return from([graphic.attributes]);
    }
  }

  /**
   * @name updateAttributes
   * @param layerId
   * @param graphic
   * @param fields
   * @returns {Observable<any>}
   * @description Updates the attributes based on the layer-id
   * If you need to update attributes on a specific feature, you have to extend this method.
   */
  public updateAttributes(layerId: string, graphic: __esri.Graphic, fields: any): Observable<any> {
    switch (layerId) {
      case LayerCode.f_verkaufsobjekte:
        return from([graphic]);
      default:
        return from([graphic]);
    }
  }

  /**
   * @name updateFeatureAttributes
   * @param graphic
   * @param lineNummber
   * @returns {Observable<any>}
   * @description Updates the line attributes
   */
  public updateFeatureAttributes(graphic: __esri.Graphic, lineNummber?: number): Observable<any> {
    const attributes = this.getGraphicAttributes(graphic);
    return this.setLineAttributes(graphic, attributes, lineNummber).pipe(
      map(() => attributes),
      catchError(error => {
        console.error('Error updating feature attributes:', error);
        return of(attributes);
      })
    );
  }

  public getEGRIDFromAV(
    geometry: __esri.Geometry,
    transObjektArt: string
  ): Observable<{ EGRIS_EGRID: string } | null> {
    let url: string;
    if (transObjektArt === TransaktionsObjektType.Liegenschaft) {
      url = this.layerContainerService.getIdentifyLayerUrl(LayerCode.liegenschaft_grundstuecke);
    } else if (transObjektArt === TransaktionsObjektType.Baurecht) {
      url = this.layerContainerService.getIdentifyLayerUrl(
        LayerCode.liegenschaften_selbststaendige_rechte
      );
    } else {
      return of(null);
    }
    if (!url) return of(null);
    const query = this.queryService.buildContainsGeometryQuery(geometry, false);
    return this.queryService.execute(url, query, 'egrid-query execute').pipe(
      map((featureSet: __esri.FeatureSet) => {
        if (featureSet.features.length === 1) {
          return { EGRIS_EGRID: featureSet.features[0].attributes.egris_egrid };
        }
        return { EGRIS_EGRID: '' };
      }),
      catchError(error => {
        console.error('Error fetching EGRID: ', error);
        return of(null);
      })
    );
  }

  /**
   * @name getAttributes
   * @param graphic
   * @returns {Observable<any>}
   * @description Returns the Verkaufsobjekte features default attributes
   */
  private getAttributes(graphic: __esri.Graphic): Observable<any> {
    const attributes = this.getGraphicAttributes(graphic);
    // update line info attributes
    const lineInfoObservable: Observable<any> = this.setLineAttributes(graphic, attributes);
    // return attributes
    return lineInfoObservable.pipe(map(el => attributes));
  }

  /**
   * @name getGraphicAttributes
   * @param graphic
   * @description Returns the Verkaufsobjekte features attributes
   */
  private getGraphicAttributes(graphic: __esri.Graphic): any {
    const attributes = { ...graphic.attributes };

    // update coordinates
    const polygon: __esri.Polygon = graphic.geometry as __esri.Polygon;

    if (!polygon) return;

    attributes.NORTHING = polygon.centroid.y.toFixed(1);
    attributes.EASTING = polygon.centroid.x.toFixed(1);

    // Fläche berechnen in m2
    attributes.FLAECHE_BERECHNET = Math.round(this.getPlanarAreaOnPolygon(polygon) * 10) / 10;

    return attributes;
  }

  /**
   * @name getPlanarAreaOnPolygon
   * @param polygon
   * @returns Area(nummber)
   * @description calculate area on polygon
   */
  private getPlanarAreaOnPolygon(polygon: __esri.Polygon): number {
    const area = EsriGeometryEngine.planarArea(polygon, 'square-meters');
    return Math.abs(area);
  }

  /**
   * @name setLineAttributes
   * @param graphic
   * @param attributes
   * @param LinienNr
   * @returns {Observable<any>}
   * @description Updates the line info attributes
   */
  private setLineAttributes(
    graphic: __esri.Graphic,
    attributes: any,
    LinienNr?: number
  ): Observable<any> {
    if (!graphic.geometry) return of(null);

    return this.lineInfoService.getLineInfo(graphic.geometry, LinienNr).pipe(
      map(lineInfo => {
        if (lineInfo.status === 400) {
          attributes.KM_ANF = '';
          attributes.KM_ENDE = '';
          attributes.LINIE = '';
          attributes.KM = '';
          this.messageService.error('NOTIFICATIONS.ERROR.BAD_LINE_NUMBER_400', 5000);
        } else if (lineInfo.status === 500) {
          attributes.KM_ANF = '';
          attributes.KM_ENDE = '';
          attributes.LINIE = '';
          attributes.KM = '';
          this.messageService.error('NOTIFICATIONS.ERROR.BAD_LINE_NUMBER_500', 5000);
        } else {
          attributes.KM_ANF = (lineInfo.km_von / 1000).toFixed(3);
          attributes.KM_ENDE = (lineInfo.km_bis / 1000).toFixed(3);
          attributes.LINIE = lineInfo.lineNumber;
          attributes.KM = lineInfo.km;
        }
        return attributes;
      })
    );
  }
}
